import Vue from 'vue'
import VueRouter from 'vue-router'
import HomeView from '@/modules/Home/HomeView.vue'
import store from '@/plugins/store'
import { intersection } from 'lodash'

Vue.use(VueRouter)

const routes = [
  {
    path: '/',
    name: 'CDSEO: SEO for the AI Era',
    component: HomeView
  },
  {
    name: 'ContentDashboard',
    path: '/content',
    component: () => import('@/modules/Content/ContentDashboardView.vue'),
    meta: {
      hidden: true,
      nav: 'content',
      name: 'Content SEO',
      breadcrumb_name: 'Content',
      breadcrumb_path: '/content',
      roles: ['ADMIN', 'CONTENT']
    }
  },
  {
    name: 'ContentCollections',
    path: '/content/collections',
    component: () => import('@/modules/Content/CollectionsView.vue'),
    meta: {
      nav: 'content',
      name: 'Collections',
      icon: 'fa-solid fa-folder',
      description: 'Craft compelling, SEO-optimized content to enhance your collection pages and drive organic traffic and AI search visibility.',
      type: 'COLLECTION',
      breadcrumb_name: 'Content',
      breadcrumb_path: '/content',
      roles: ['ADMIN', 'CONTENT'],
      modules: ['CONTENT-COLLECTION']
    }
  },
  {
    name: 'ContentProducts',
    path: '/content/products',
    component: () => import('@/modules/Content/ProductsView.vue'),
    meta: {
      nav: 'content',
      name: 'Products',
      icon: 'fa-solid fa-sack-dollar',
      description: 'Generate detailed, SEO-friendly product content that captivates, converts, and improves search rankings and AI visibility.',
      type: 'PRODUCT',
      breadcrumb_name: 'Content',
      breadcrumb_path: '/content',
      roles: ['ADMIN', 'CONTENT'],
      modules: ['CONTENT-PRODUCT']
    }
  },
  {
    name: 'ContentArticles',
    path: '/content/articles',
    component: () => import('@/modules/Content/ArticlesView.vue'),
    meta: {
      nav: 'content',
      name: 'Blog Articles',
      icon: 'fa-solid fa-file-alt',
      description: 'Write high-quality articles that engage your audience and maximizes visibility across Google and AI search.',
      type: 'ARTICLE',
      breadcrumb_name: 'Content',
      breadcrumb_path: '/content',
      roles: ['ADMIN', 'CONTENT'],
      modules: ['CONTENT-ARTICLE']
    }
  },
  {
    name: 'ContentPages',
    path: '/content/pages',
    component: () => import('@/modules/Content/PagesView.vue'),
    meta: {
      nav: 'content',
      name: 'Pages',
      icon: 'fa-solid fa-file',
      description: 'Make SEO-enhanced content pages that improve user experience and increases search engine visibility.',
      type: 'PAGE',
      breadcrumb_name: 'Content',
      breadcrumb_path: '/content',
      roles: ['ADMIN', 'CONTENT'],
      modules: ['CONTENT-PAGE']
    }
  },
  // {
  //   name: 'ContentBrief',
  //   path: '/content/brief',
  //   component: () => import('@/modules/Content/ContentBriefView.vue'),
  //   meta: {
  //     nav: 'content',
  //     name: 'Create Brief',
  //     icon: 'fa-solid fa-list',
  //     description: 'Utilize AI to create a content brief.',
  //     breadcrumb_name: 'Content',
  //     breadcrumb_path: '/content',
  //     roles: ['SUPERUSER']
  //   }
  // },
  {
    name: 'ContentThinContent',
    path: '/content/thin-content',
    component: () => import('@/modules/Content/ThinContent/ThinContentView.vue'),
    meta: {
      hiddenFromDashboard: true,
      nav: 'content',
      name: 'Thin Content',
      description: 'Identify and edit or block low quality pages.',
      icon: 'fa-solid fa-eye-slash',
      breadcrumb_name: 'Content',
      breadcrumb_path: '/content',
      roles: ['ADMIN', 'CONTENT'],
      divider_hr: true
    }
  },
  {
    name: 'ContentPageEditor',
    path: '/content/edit/:pageId',
    component: () => import('@/modules/Content/PageEditor'),
    meta: {
      hidden: true,
      nav: 'content',
      navCollapse: true,
      name: 'Content Page Editor',
      noPadding: true,
      roles: ['ADMIN', 'CONTENT']
    }
  },
  {
    name: 'TechnicalDashboard',
    path: '/tech',
    component: () => import('@/modules/Technical/TechnicalDashboardView.vue'),
    meta: {
      hidden: true,
      nav: 'tech',
      name: 'Technical SEO',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['ADMIN', 'TECHNICAL']
    }
  },
  // {
  //   name: 'TechnicalOptimizeListings',
  //   path: '/tech/optimize-listings',
  //   component: () => import('@/modules/Technical/OptimizeListings/OptimizeListingsView.vue'),
  //   meta: {
  //     nav: 'tech',
  //     name: 'Optimize Listings',
  //     description: 'Optimize your pages for high search engine ranking.',
  //     icon: 'fa-solid fa-list-check',
  //     breadcrumb_name: 'Tech',
  //     breadcrumb_path: '/tech',
  //     roles: ['ADMIN', 'TECHNICAL']
  //   }
  // },
  {
    name: 'TechnicalOptimizeProducts',
    path: '/tech/optimize-products',
    component: () => import('@/modules/Technical/OptimizeListings/OptimizeListingsView.vue'),
    meta: {
      nav: 'tech',
      name: 'Optimize Products',
      type: 'Product',
      description: 'Improve product titles and meta descriptions for improved ranking and visibility.',
      icon: 'fa-solid fa-sack-dollar',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['ADMIN', 'TECHNICAL']
    }
  },
  {
    name: 'TechnicalOptimizeCollections',
    path: '/tech/optimize-collections',
    component: () => import('@/modules/Technical/OptimizeListings/OptimizeListingsView.vue'),
    meta: {
      nav: 'tech',
      name: 'Optimize Collections',
      type: 'Collection',
      description: 'Improve collection titles and meta descriptions for improved ranking and visibility.',
      icon: 'fa-solid fa-layer-group',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['ADMIN', 'TECHNICAL']
    }
  },
  {
    name: 'TechnicalOptimizePages',
    path: '/tech/optimize-pages',
    component: () => import('@/modules/Technical/OptimizeListings/OptimizeListingsView.vue'),
    meta: {
      nav: 'tech',
      name: 'Optimize Pages',
      type: 'Page',
      description: 'Improve pages titles and meta descriptions for improved ranking and visibility.',
      icon: 'fa-solid fa-file-lines',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['ADMIN', 'TECHNICAL']
    }
  },
  {
    name: 'TechnicalOptimizeArticles',
    path: '/tech/optimize-articles',
    component: () => import('@/modules/Technical/OptimizeListings/OptimizeListingsView.vue'),
    meta: {
      nav: 'tech',
      name: 'Optimize Articles',
      type: 'Article',
      description: 'Improve blog article titles and meta descriptions for improved ranking and visibility.',
      icon: 'fa-solid fa-pen-nib',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['ADMIN', 'TECHNICAL'],
    }
  },

  // ...(['Product', 'Collection', 'Page', 'Article'].map(type => ({
  //   name: `TechnicalOptimize${type}s`,
  //   path: `/tech/optimize-${type.toLowerCase()}s`,
  //   component: () => import('@/modules/Technical/OptimizeListings/OptimizeListingsView.vue'),
  //   meta: {
  //     nav: 'tech',
  //     name: `Optimize ${type}s`,
  //     type,
  //     description: 'x',
  //     icon: 'fa-solid fa-list-check',
  //     breadcrumb_name: 'Tech',
  //     breadcrumb_path: '/tech',
  //     roles: ['ADMIN', 'TECHNICAL']
  //   }
  // }))),
  {
    name: 'TechnicalRepairLinks',
    path: '/tech/repair-links',
    component: () => import('@/modules/Technical/RepairLinks/RepairLinksView.vue'),
    meta: {
      nav: 'tech',
      name: 'Repair Links',
      icon: 'fa-solid fa-link',
      description: 'Maintain link equity by preventing broken links.',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['ADMIN', 'TECHNICAL'],
      divider_hr: true
    }
  },
  {
    name: 'TechnicalIndexIntegrity',
    path: '/tech/index-integrity',
    component: () => import('@/modules/Technical/IndexIntegrity/IndexIntegrityView.vue'),
    meta: {
      nav: 'tech',
      name: 'Index Integrity',
      description: 'Audit and clean up which pages search engines can index.',
      icon: 'fa-solid fa-clipboard-check',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['SUPERUSER']
    }
  },
  {
    name: 'TechnicalImageOptimization',
    path: '/tech/image-optimization',
    component: () => import('@/modules/Technical/ImageOptimization/ImageOptimizationView.vue'),
    meta: {
      nav: 'tech',
      name: 'Optimize Images',
      description: 'Improve page speed, on-page SEO and show in image search.',
      icon: 'fa-solid fa-image',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['SUPERUSER'],
      modules: ['TECHNICAL-IMAGES']
    }
  },
  {
    name: 'TechnicalRichResults',
    path: '/tech/rich-results',
    component: () => import('@/modules/Technical/RichResults/RichResultsView.vue'),
    meta: {
      nav: 'tech',
      // name: 'Broken Links',
      name: 'Structured Data',
      icon: 'fa-solid fa-folder-tree',
      description: 'Improve visibility with structured data that feeds content to search and AI engines.',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['ADMIN', 'TECHNICAL']
    }
  },
  {
    name: 'TechnicalSiteVerification',
    path: '/tech/site-verification',
    component: () => import('@/modules/Technical/SiteVerification/SiteVerificationView.vue'),
    meta: {
      nav: 'tech',
      name: 'Site Verification',
      icon: 'fa-solid fa-shield',
      description: 'Gain valuable insights into your search peformance.',
      breadcrumb_name: 'Tech',
      breadcrumb_path: '/tech',
      roles: ['ADMIN', 'TECHNICAL'],
      divider_hr: true
    }
  },
  {
    name: 'Activity',
    path: '/activity',
    component: () => import('@/modules/Activity/ActivityView.vue'),
    meta: {
      nav: 'activity',
      hidden: true,
      roles: ['SUPERUSER'] // TODO: Add 'ADMIN' when ready
    }
  },
  {
    name: 'ActivityReport',
    path: '/activity/report',
    component: () => import('@/modules/Activity/ActivityReportView.vue'),
    meta: {
      nav: 'activity',
      name: 'SEO Report',
      icon: 'fa-solid fa-file-code',
      description: 'Generate a report to share SEO progress and key updates.',
      breadcrumb_name: 'Activity',
      breadcrumb_path: '/activity',
      roles: ['SUPERUSER'] // TODO: Add 'ADMIN' when ready
    }
  },
  {
    name: 'RankTracking',
    path: '/rank-tracking',
    component: () => import('@/modules/RankTracking/RankTrackingView.vue'),
    meta: {
      nav: 'activity',
      name: 'Rank Tracking',
      icon: 'fa-solid fa-chart-line',
      description: 'Monitor search engine rankings for tracked keywords.',
      breadcrumb_name: 'Activity',
      breadcrumb_path: '/activity',
      roles: ['SUPERUSER']
    }
  },
  {
    name: 'RankTrackingPageDetail',
    path: '/rank-tracking/page/:pageId',
    component: () => import('@/modules/RankTracking/PageDetailView.vue'),
    meta: {
      nav: 'activity',
      hidden: true,
      name: 'Page Rankings',
      breadcrumb_name: 'Rank Tracking',
      breadcrumb_path: '/rank-tracking',
      roles: ['SUPERUSER']
    }
  },
  {
    name: 'ActivityChangeLog',
    path: '/activity/changelog',
    component: () => import('@/modules/Activity/ChangeLog/ChangeLog.vue'),
    meta: {
      nav: 'activity',
      name: 'Changelog',
      icon: 'fa-solid fa-timeline',
      description: 'View the complete list of changelog activity.',
      breadcrumb_name: 'Activity',
      breadcrumb_path: '/activity',
      roles: ['SUPERUSER'] // TODO: Add 'ADMIN' when ready
    }
  },
  {
    name: 'Settings',
    path: '/settings',
    component: () => import('@/modules/Settings/SettingsView.vue'),
    meta: {
      nav: 'settings',
      hidden: true
    }
  },
  {
    name: 'SettingsWorkspace',
    path: '/settings/workspace',
    component: () => import('@/modules/Settings/WorkspaceView.vue'),
    meta: {
      nav: 'settings',
      name: 'Workspace',
      icon: 'fa-solid fa-cog',
      breadcrumb_name: 'Settings',
      roles: ['ADMIN']
    }
  },
  {
    name: 'SettingsMembers',
    path: '/settings/members',
    component: () => import('@/modules/Settings/MembersView'),
    meta: {
      nav: 'settings',
      name: 'Members',
      icon: 'fa-solid fa-users',
      breadcrumb_name: 'Settings',
      roles: ['ADMIN']
    }
  },
  {
    name: 'SettingsBilling',
    path: '/settings/billing',
    component: () => import('@/modules/Settings/Billing/BillingView.vue'),
    meta: {
      nav: 'settings',
      name: 'Plan & Billing',
      icon: 'fa-solid fa-sack-dollar',
      breadcrumb_name: 'Billing',
      roles: ['ADMIN']
    }
  },
  {
    name: 'SettingsIntegrations',
    path: '/settings/integrations',
    component: () => import('@/modules/Settings/Integrations/IntegrationsView.vue'),
    meta: {
      nav: 'settings',
      name: 'Integrations',
      icon: 'fa-solid fa-plug',
      breadcrumb_name: 'Settings',
      roles: ['SUPERUSER']
    }
  },
  {
    name: 'SettingsProfile',
    path: '/settings/profile',
    component: () => import('@/modules/Settings/ProfileView.vue'),
    meta: {
      nav: 'settings',
      name: 'Profile',
      icon: 'fa-solid fa-user',
      breadcrumb_name: 'Settings',
      divider_hr: true,
    }
  },
  {
    name: 'SettingsLogout',
    path: '/logout',
    meta: {
      nav: 'settings',
      name: 'Log Out',
      icon: 'fa-solid fa-arrow-right-from-bracket',
    }
  },
  {
    name: 'AdminDashboard',
    path: '/admin',
    component: () => import('@/modules/Admin/AdminDashboardView.vue'),
    meta: {
      hidden: true,
      nav: 'admin',
      name: 'Global Controls',
      breadcrumb_name: 'Admin',
      breadcrumb_path: '/admin',
      roles: ['SUPERUSER'],
      layout: 'admin'
    }
  },
  {
    name: 'AdminAccounts',
    path: '/admin/accounts',
    component: () => import('@/modules/Admin/AdminAccountsView.vue'),
    meta: {
      nav: 'admin',
      name: 'Workspaces',
      icon: 'fa-solid fa-layer-group',
      description: 'View and manage all customer workspaces.',
      breadcrumb_name: 'System Admin',
      breadcrumb_path: '/admin',
      roles: ['SUPERUSER'],
      layout: 'admin'
    }
  },
  {
    name: 'AdminSuperAdmins',
    path: '/admin/super-admins',
    component: () => import('@/modules/Admin/AdminSuperAdminsView.vue'),
    meta: {
      nav: 'admin',
      name: 'Super Admins',
      icon: 'fa-solid fa-user-shield',
      description: 'View and manage super administrator users.',
      breadcrumb_name: 'System Admin',
      breadcrumb_path: '/admin',
      roles: ['SUPERUSER'],
      layout: 'admin'
    }
  },
  {
    name: 'AdminTasks',
    path: '/admin/tasks',
    component: () => import('@/modules/Admin/AdminTasksView.vue'),
    meta: {
      nav: 'admin',
      name: 'Task Queue',
      icon: 'fa-solid fa-list-check',
      description: 'View and manage the background task queue.',
      breadcrumb_name: 'System Admin',
      breadcrumb_path: '/admin',
      roles: ['SUPERUSER'],
      layout: 'admin'
    }
  },
  {
    path: "/:pathMatch(.*)*", // Catch-all route for 404
    name: "404Error",
    meta: {
      name: '404 - Page Not Found'
    },
    component: () => import('@/modules/404Error.vue'),
  }
]

const router = new VueRouter({
  mode: 'history',
  base: '/',
  routes,
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  }
})

const getSubdomain = () => {
  const host = window.location.host
  // Handle localhost with port (e.g., app.local.localhost:8080)
  if (host.includes('localhost')) {
    const hostWithoutPort = host.split(':')[0]
    return hostWithoutPort.split('.')[0]
  }
  // Handle production domain (e.g., app.cdseo.com)
  return host.split('.')[0]
}

const beforeEach = (to, from, next) => {
  // Check if accessing admin routes - must be on app subdomain
  if (to.path.startsWith('/admin')) {
    const subdomain = getSubdomain()
    if (subdomain !== 'app') {
      try {
        next({ path: '/404' })
        return
      } catch (e) {
        console.warn(e)
      }
    }
  }

  const roles = store.getters?.roles || []
  const modules = store.getters?.modules || []

  // Check if route requires authentication (has roles or modules requirement)
  const requiresAuth = to.meta?.roles?.length || to.meta?.modules?.length

  // If route requires auth but user is not logged in, redirect to home
  if (requiresAuth && !store.getters?.user?.id) {
    try {
      next({ path: '/' })
      return
    } catch (e) {
      console.warn(e)
    }
  }

  // Superusers bypass both role and module checks
  if (
    store.getters?.user?.id &&
    !roles.includes('SUPERUSER') &&
    (
      (
        to.meta?.roles?.length &&
        !intersection(roles, to.meta.roles).length
      ) ||
      (
        to.meta?.modules?.length &&
        intersection(modules, to.meta.modules).length !== to.meta.modules.length
      )
    )
  ) {
    if (to.meta.fallback) {
      try {
        next({ path: to.meta.fallback })
      } catch (e) {
        console.warn(e)
      }
    } else {
      try {
        next({ path: '/' })
      } catch (e) {
        console.warn(e)
      }
    }
  } else {
    next()
  }
}

router.beforeEach(beforeEach)

router.afterEach((to) => {
  if (to?.meta?.navCollapse) {
    store.commit('setNavCollapse', true)
  } else {
    store.commit('setNavCollapse', false)
  }

  if (store.getters.showAuth) {
    document.title = 'Dashboard Login | CDSEO'
    return
  }
  else if (!to) {
    document.title = 'CDSEO'
    return
  }
  document.title = (to.meta?.title || to.meta?.name || to.name) + ' - ' + window.location.hostname + ' | CDSEO'
})

export default router

window.$router = router

export {
  routes,
  beforeEach
}